import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NbDialogService } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ReportFilterState, toApiParams } from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-order-management',
  templateUrl: './order-management.component.html',
  styleUrls: ['./order-management.component.scss']
})
export class OrderManagementComponent implements OnInit, OnDestroy {

  @Input() storeId: string = '';
  @Input() set initialOrderNumber(value: string | null | undefined) {
    if (value && value !== this.orderNumberSearch) {
      this.orderNumberSearch = value;
      if (this.storeId) {
        this.currentPage = 1;
        this.loadOrders();
      }
    }
  }

  role: string = localStorage.getItem('role') || '';

  orders: any[] = [];
  orderStatistics: any = null;
  loading: boolean = false;
  
  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalOrders: number = 0;
  pageSize: number = 20;
  
  // Filters
  selectedStatus: string = '';
  selectedPaymentStatus: string = '';
  startDate: string = '';
  endDate: string = '';
  customerSearch: string = '';
  orderNumberSearch: string = '';
  filterLateWebhookAfterCancel: boolean = false;
  sortBy: string = 'orderDate';
  sortOrder: string = 'desc';
  
  // UI State
  showFilters: boolean = false;
  selectedOrders: Set<string> = new Set();
  bulkAction: string = '';
  
  // Order Details Modal
  showOrderDetails: boolean = false;
  selectedOrder: any = null;
  
  // Status Update
  showStatusModal: boolean = false;
  statusForm!: FormGroup;
  
  // Refund Modal
  showRefundModal: boolean = false;
  refundForm!: FormGroup;
  
  // Auto-refresh
  autoRefreshEnabled: boolean = true;
  refreshIntervalSec: number = 30;
  exportFormat: 'csv' | 'xlsx' = 'csv';
  exportFilter: ReportFilterState = { period: 'this_month' };
  exportPaymentStatus: string = '';
  exportPaymentMethod: string = '';
  private refreshSubscription?: Subscription;
  private countdownSub?: Subscription;
  private ordersLoadSeq = 0;
  private ordersSilentLoadSeq = 0;
  countdown: number = 0;

  // Make Math available in template
  Math = Math;

  // Available statuses
  orderStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'paid', label: 'Paid' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' }
  ];

  constructor(
    private ecommerceService: EcommerceService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initializeForms();
    if (this.storeId) {
      this.loadOrders();
      this.loadOrderStatistics();
    }
    this.countdown = this.refreshIntervalSec;
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdown = this.refreshIntervalSec;

    this.refreshSubscription = interval(this.refreshIntervalSec * 1000).subscribe(() => {
      if (this.storeId) {
        this.loadOrdersSilent();
        this.loadOrderStatistics();
      }
      this.countdown = this.refreshIntervalSec;
    });

    this.countdownSub = interval(1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      }
    });
  }

  stopAutoRefresh(): void {
    this.refreshSubscription?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.refreshSubscription = undefined;
    this.countdownSub = undefined;
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  onRefreshIntervalChange(): void {
    this.countdown = this.refreshIntervalSec;
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  /** Silent reload — does not set this.loading so there is no spinner flash. */
  private loadOrdersSilent(): void {
    if (this.loading) return;
    const seq = ++this.ordersSilentLoadSeq;
    const userSeq = this.ordersLoadSeq;
    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...(this.selectedStatus && { status: this.selectedStatus }),
      ...(this.selectedPaymentStatus && { paymentStatus: this.selectedPaymentStatus }),
      ...(this.startDate && { startDate: this.startDate }),
      ...(this.endDate && { endDate: this.endDate }),
      ...(this.customerSearch && { customer: this.customerSearch }),
      ...(this.orderNumberSearch && { orderNumber: this.orderNumberSearch }),
      ...(this.filterLateWebhookAfterCancel && { lateWebhookAfterCancel: true })
    };

    this.ecommerceService.getStoreOrders(this.storeId, params).subscribe(
      (res: any) => {
        if (seq !== this.ordersSilentLoadSeq || userSeq !== this.ordersLoadSeq) return;
        if (res.response && res.response.success && res.response.data) {
          this.orders = res.response.data.orders || [];
          this.totalOrders = res.response.data.pagination?.totalOrders || 0;
          this.totalPages = res.response.data.pagination?.totalPages || 1;
          this.currentPage = res.response.data.pagination?.currentPage || 1;
        } else if (res.code === 200 && res.data) {
          this.orders = res.data.orders || [];
          this.totalOrders = res.data.pagination?.totalOrders || 0;
          this.totalPages = res.data.pagination?.totalPages || 1;
          this.currentPage = res.data.pagination?.currentPage || 1;
        }
      },
      (error) => {
        if (seq !== this.ordersSilentLoadSeq || userSeq !== this.ordersLoadSeq) return;
        console.error('Silent refresh failed:', error);
      }
    );
  }

  initializeForms(): void {
    this.statusForm = new FormGroup({
      status: new FormControl('', [Validators.required]),
      adminNotes: new FormControl(''),
      trackingNumber: new FormControl(''),
      carrier: new FormControl('')
    });

    this.refundForm = new FormGroup({
      refundType: new FormControl('partial', [Validators.required]),
      amount: new FormControl(0, [Validators.required, Validators.min(0.01)]),
      reason: new FormControl('', [Validators.required])
    });
  }

  loadOrders(): void {
    this.ordersSilentLoadSeq++;
    const seq = ++this.ordersLoadSeq;
    this.loading = true;

    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...(this.selectedStatus && { status: this.selectedStatus }),
      ...(this.selectedPaymentStatus && { paymentStatus: this.selectedPaymentStatus }),
      ...(this.startDate && { startDate: this.startDate }),
      ...(this.endDate && { endDate: this.endDate }),
      ...(this.customerSearch && { customer: this.customerSearch }),
      ...(this.orderNumberSearch && { orderNumber: this.orderNumberSearch }),
      ...(this.filterLateWebhookAfterCancel && { lateWebhookAfterCancel: true })
    };

    this.ecommerceService.getStoreOrders(this.storeId, params).pipe(
      finalize(() => {
        if (seq === this.ordersLoadSeq) {
          this.loading = false;
        }
      })
    ).subscribe(
      (res: any) => {
        if (seq !== this.ordersLoadSeq) return;
        console.log('Orders API response:', res);

        if (res.response && res.response.success && res.response.data) {
          this.orders = res.response.data.orders || [];
          this.totalOrders = res.response.data.pagination?.totalOrders || 0;
          this.totalPages = res.response.data.pagination?.totalPages || 1;
          this.currentPage = res.response.data.pagination?.currentPage || 1;
        } else if (res.code === 200 && res.data) {
          this.orders = res.data.orders || [];
          this.totalOrders = res.data.pagination?.totalOrders || 0;
          this.totalPages = res.data.pagination?.totalPages || 1;
          this.currentPage = res.data.pagination?.currentPage || 1;
        } else {
          console.warn('Unexpected orders response format:', res);
          this.orders = [];
        }
      },
      (error) => {
        if (seq !== this.ordersLoadSeq) return;
        console.error('Failed to load orders:', error);
        this.toastr.error('Failed to load orders');
      }
    );
  }

  loadOrderStatistics(): void {
    const params = {
      period: 'month',
      comparison: true
    };

    this.ecommerceService.getOrderStatistics(this.storeId, params).subscribe(
      (res: any) => {
        console.log('Order statistics response:', res);
        
        if (res.response && res.response.success && res.response.data) {
          this.orderStatistics = res.response.data;
        } else if (res.code === 200 && res.data) {
          this.orderStatistics = res.data;
        }
      },
      (error) => {
        console.error('Failed to load order statistics:', error);
      }
    );
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadOrders();
  }

  onSortChange(): void {
    this.loadOrders();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrders();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.selectedPaymentStatus = '';
    this.startDate = '';
    this.endDate = '';
    this.customerSearch = '';
    this.orderNumberSearch = '';
    this.filterLateWebhookAfterCancel = false;
    this.onFilterChange();
  }

  viewOrderDetails(order: any): void {
    this.selectedOrder = order;
    this.showOrderDetails = true;
  }

  closeOrderDetails(): void {
    this.showOrderDetails = false;
    this.selectedOrder = null;
  }

  openStatusModal(order: any): void {
    this.stopAutoRefresh();
    this.selectedOrder = order;
    this.statusForm.patchValue({
      status: order.status,
      adminNotes: '',
      trackingNumber: order.trackingNumber || '',
      carrier: order.carrier || ''
    });
    this.showStatusModal = true;
  }

  closeStatusModal(): void {
    this.showStatusModal = false;
    this.selectedOrder = null;
    this.statusForm.reset();
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  updateOrderStatus(): void {
    if (this.statusForm.invalid) {
      this.markFormGroupTouched(this.statusForm);
      this.toastr.error('Please fill in all required fields');
      return;
    }

    this.spinner.show();
    const formValue = this.statusForm.value;
    
    const statusData = {
      status: formValue.status,
      adminNotes: formValue.adminNotes || `Status updated to ${formValue.status} by admin`,
      ...(formValue.trackingNumber && { trackingNumber: formValue.trackingNumber }),
      ...(formValue.carrier && { carrier: formValue.carrier })
    };

    this.ecommerceService.updateOrderStatus(this.selectedOrder._id, statusData).subscribe(
      (res: any) => {
        console.log('Update status response:', res);
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Order status updated successfully');
          this.closeStatusModal();
          this.loadOrders();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to update order status:', error);
        this.toastr.error('Failed to update order status: ' + (error.error?.message || error.message));
        this.spinner.hide();
      }
    );
  }

  openRefundModal(order: any): void {
    this.stopAutoRefresh();
    this.selectedOrder = order;
    this.refundForm.patchValue({
      refundType: 'partial',
      amount: order.finalAmount,
      reason: ''
    });
    this.showRefundModal = true;
  }

  closeRefundModal(): void {
    this.showRefundModal = false;
    this.selectedOrder = null;
    this.refundForm.reset();
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  processRefund(): void {
    if (this.refundForm.invalid) {
      this.markFormGroupTouched(this.refundForm);
      this.toastr.error('Please fill in all required fields');
      return;
    }

    const formValue = this.refundForm.value;
    
    Swal.fire({
      title: 'Process Refund?',
      text: `Refund ${formValue.amount} DKK for order ${this.selectedOrder.orderNumber}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, process refund',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performRefund(formValue);
      }
    });
  }

  performRefund(refundData: any): void {
    this.spinner.show();
    
    this.ecommerceService.processRefund(this.selectedOrder._id, refundData).subscribe(
      (res: any) => {
        console.log('Refund response:', res);
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Refund processed successfully');
          this.closeRefundModal();
          this.loadOrders();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to process refund:', error);
        this.toastr.error('Failed to process refund: ' + (error.error?.message || error.message));
        this.spinner.hide();
      }
    );
  }

  onExportFilterChange(filter: ReportFilterState): void {
    this.exportFilter = filter;
  }

  onExportOrders(event: { format: string }): void {
    this.spinner.show();
    const params: any = {
      format: event.format,
      includeItems: 'true',
      ...toApiParams(this.exportFilter),
      ...(this.selectedStatus && { status: this.selectedStatus }),
    };
    if (this.exportPaymentStatus) params.paymentStatus = this.exportPaymentStatus;
    if (this.exportPaymentMethod) params.paymentMethod = this.exportPaymentMethod;

    this.ecommerceService.exportOrders(this.storeId, params).subscribe({
      next: (blob: Blob) => {
        const filename = `orders-export-${this.storeId}-${new Date().toISOString().split('T')[0]}.${event.format}`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.spinner.hide();
        this.toastr.success(this.translate.instant('KPI.ORDERS.EXPORT_SUCCESS'));
      },
      error: (error: any) => {
        console.error('Failed to export orders:', error);
        this.spinner.hide();
        if (error?.error instanceof Blob) {
          error.error.text().then((text: string) => {
            try {
              const parsed = JSON.parse(text);
              this.toastr.error(parsed?.response?.message || parsed?.message || 'Failed to export orders');
            } catch {
              this.toastr.error(text || 'Failed to export orders');
            }
          }).catch(() => this.toastr.error('Failed to export orders'));
        } else {
          this.toastr.error(error?.error?.response?.message || 'Failed to export orders');
        }
      }
    });
  }

  exportOrders(): void {
    this.onExportOrders({ format: this.exportFormat });
  }

  toggleOrderSelection(orderId: string): void {
    if (this.selectedOrders.has(orderId)) {
      this.selectedOrders.delete(orderId);
    } else {
      this.selectedOrders.add(orderId);
    }
  }

  selectAllOrders(): void {
    if (this.selectedOrders.size === this.orders.length) {
      this.selectedOrders.clear();
    } else {
      this.orders.forEach(order => {
        this.selectedOrders.add(order._id);
      });
    }
  }

  executeBulkAction(): void {
    if (!this.bulkAction || this.selectedOrders.size === 0) {
      this.toastr.warning('Please select orders and an action');
      return;
    }

    const selectedOrderIds = Array.from(this.selectedOrders);
    
    switch (this.bulkAction) {
      case 'update_status':
        this.bulkUpdateStatus(selectedOrderIds);
        break;
      case 'mark_urgent':
        this.bulkMarkUrgent(selectedOrderIds);
        break;
      case 'add_notes':
        this.bulkAddNotes(selectedOrderIds);
        break;
      default:
        this.toastr.info('Bulk action will be implemented');
    }
  }

  bulkUpdateStatus(orderIds: string[]): void {
    // Implementation for bulk status update
    this.toastr.info('Bulk status update will be implemented');
  }

  bulkMarkUrgent(orderIds: string[]): void {
    const bulkData = {
      operation: 'mark_urgent',
      filters: { orderIds },
      updates: { isUrgent: true }
    };

    this.ecommerceService.bulkUpdateOrders(this.storeId, bulkData).subscribe(
      (res: any) => {
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success(`${orderIds.length} orders marked as urgent`);
          this.loadOrders();
          this.selectedOrders.clear();
        }
      },
      (error) => {
        console.error('Failed to mark orders as urgent:', error);
        this.toastr.error('Failed to update orders');
      }
    );
  }

  bulkAddNotes(orderIds: string[]): void {
    // Implementation for bulk notes addition
    this.toastr.info('Bulk add notes will be implemented');
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'success';
      case 'paid': return 'info';
      case 'processing': return 'warning';
      case 'shipped': return 'primary';
      case 'cancelled': return 'danger';
      case 'refunded': return 'basic';
      case 'pending': return 'warning';
      default: return 'basic';
    }
  }

  getPaymentStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'danger';
      case 'refunded': return 'basic';
      default: return 'basic';
    }
  }

  formatPaymentMethod(method: string | undefined): string {
    if (!method) {
      return '—';
    }
    const labels: Record<string, string> = {
      card: 'Card',
      card_terminal: 'Card Terminal',
      cash: 'Cash',
      mobile: 'Mobile',
      mobilepay: 'MobilePay',
      wallet: 'Wallet',
      split: 'Split',
    };
    return labels[method] || method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatCurrency(amount: number): string {
    if (!amount) return '0.00 DKK';
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  trackByOrderId(index: number, order: any): string {
    return order._id;
  }
}